import { getApps, initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  addDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  limit,
  getDocs,
  connectFirestoreEmulator,
  arrayUnion,
  onSnapshot
} from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { withSentrySpan } from './sentry';

const isLocalhost = typeof window !== 'undefined' && Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.startsWith('192.168.') ||
  window.location.hostname.endsWith('.local')
);

const fbConsole = isLocalhost
  ? console
  : { log: () => {}, warn: () => {}, error: () => {}, debug: () => {} };

// Helper to normalize phone numbers
export const normalizePhone = (phone?: string | null): string => {
  if (!phone) return '';
  let normalized = String(phone).replace(/[^\d+]/g, '');
  if (normalized.length === 10 && !normalized.startsWith('+')) {
    return `+1${normalized}`;
  }
  if (normalized.length === 11 && normalized.startsWith('1') && !normalized.startsWith('+')) {
    return `+${normalized}`;
  }
  return normalized;
};

const normalizeSearchText = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s_.-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const splitIdentityName = (fullName: any) => {
  const safe = String(fullName || '').trim();
  if (!safe) return { firstName: '', lastName: '' };
  const parts = safe.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

const buildCanonicalIdentityFields = (fullName: any) => {
  const { firstName, lastName } = splitIdentityName(fullName);
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return { firstName, lastName, name };
};

const resolveCanonicalIdentityName = (record: any, _phone?: string) => {
  const firstName = String(record?.firstName || '').trim();
  const lastName = String(record?.lastName || '').trim();
  const splitName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (splitName) return splitName;
  return String(record?.name || record?.fullName || record?.displayName || '').trim();
};

export const buildProfileSearchTokens = (name?: string | null, username?: string | null): string[] => {
  const tokens = new Set<string>();
  const fields = [normalizeSearchText(name || ''), normalizeSearchText(username || '')];

  fields.forEach((field) => {
    if (!field) return;
    field.split(/\s+/).forEach((word) => {
      if (!word) return;
      // Prefixes for fast incremental search.
      for (let i = 1; i <= word.length; i += 1) {
        tokens.add(word.slice(0, i));
      }
      // Trigrams for mid-string contains matching.
      if (word.length <= 3) {
        tokens.add(word);
      } else {
        for (let i = 0; i <= word.length - 3; i += 1) {
          tokens.add(word.slice(i, i + 3));
        }
      }
    });
  });

  return Array.from(tokens).slice(0, 300);
};

/**
 * PRODUCTION FIREBASE CONFIGURATION
 */
const firebaseConfig = {
  apiKey: "AIzaSyBeTf1zcBShSRhHJORQIYPCd2RrNDdIKmc",
  authDomain: "clero-b49eb.firebaseapp.com",
  projectId: "clero-b49eb",
  storageBucket: "clero-b49eb.firebasestorage.app",
  messagingSenderId: "504129993432",
  appId: "1:504129993432:web:dae5af756f7c7484db75ad",
  measurementId: "G-PXPJE5MFGB"
};

export const firebaseProjectId = firebaseConfig.projectId;
export const isUsingFirebaseEmulator =
  import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' && !import.meta.env.PROD;
const configuredEmulatorHost = String(import.meta.env.VITE_FIREBASE_EMULATOR_HOST || '').trim();
const defaultBrowserHost = typeof window !== 'undefined' ? window.location.hostname : '127.0.0.1';
const resolvedEmulatorHost = (configuredEmulatorHost || defaultBrowserHost || '127.0.0.1').replace(/^0\.0\.0\.0$/, '127.0.0.1');

import { getAuth, RecaptchaVerifier } from 'firebase/auth';
import { getAnalytics } from "firebase/analytics";

// Initialize Firebase
const app = getApps().find((candidate) => candidate.name === '[DEFAULT]') || initializeApp(firebaseConfig);
const founderAdminApp = getApps().find((candidate) => candidate.name === 'founder-admin') || initializeApp(firebaseConfig, 'founder-admin');
export const firestore = getFirestore(app);
export const functions = getFunctions(app);
export const founderAdminFunctions = getFunctions(app);
let analyticsInstance: ReturnType<typeof getAnalytics> | null = null;
try {
  if (typeof window !== 'undefined') {
    analyticsInstance = getAnalytics(app);
  }
} catch (error: any) {
  // Firebase Installations can intermittently fail (503/unavailable). Keep app flows running.
  console.warn('[Firebase] Analytics initialization skipped:', {
    code: error?.code,
    message: error?.message,
  });
}
export const analytics = analyticsInstance;

// Standard Firebase Auth Initialization
export const auth = getAuth(app);
export const founderAdminAuth = auth;

import { connectAuthEmulator } from 'firebase/auth';

// EMULATOR SUPPORT
const useEmulator = isUsingFirebaseEmulator;

if (useEmulator) {
  console.log(
    `%c[Firebase] Emulator mode enabled. Connecting to Firestore: ${resolvedEmulatorHost}:8080, Functions: ${resolvedEmulatorHost}:5001, Auth: ${resolvedEmulatorHost}:9099`,
    "color: #4CAF50; font-weight: bold;"
  );

  try {
    connectFirestoreEmulator(firestore, resolvedEmulatorHost, 8080);
    connectFunctionsEmulator(functions, resolvedEmulatorHost, 5001);
    connectFunctionsEmulator(founderAdminFunctions, resolvedEmulatorHost, 5001);
    connectAuthEmulator(auth, `http://${resolvedEmulatorHost}:9099`, { disableWarnings: true });
    connectAuthEmulator(founderAdminAuth, `http://${resolvedEmulatorHost}:9099`, { disableWarnings: true });
    auth.settings.appVerificationDisabledForTesting = true;
    founderAdminAuth.settings.appVerificationDisabledForTesting = true;
  } catch (error) {
    console.warn('[Firebase] Failed to connect one or more emulators. Falling back to currently initialized services.', error);
  }
}

// Bank service - handles bank connections and settlements (formerly dwollaService)
export const bankService = {
  async createLinkToken(
    id: {
      sessionId?: string;
      phone?: string;
      redirectUri?: string;
      androidPackageName?: string;
      mode?: 'payment' | 'payout' | 'payment_bank' | 'payouts' | 'credit_only' | 'spending';
      reconnectSourcePlaidItemId?: string;
      updateAccountSelectionEnabled?: boolean;
    },
    isUpdateMode: boolean = false
  ) {
    const createToken = httpsCallable(functions, 'createPlaidLinkToken');
    const result = await withSentrySpan(
      {
        op: 'plaid.link_token',
        name: 'plaid.create_link_token',
        attributes: {
          mode: String(id.mode || 'spending'),
          isUpdateMode: Boolean(isUpdateMode),
        },
      },
      () => createToken({ ...id, isUpdateMode }),
    );
    return result.data as { linkToken: string };
  },

  async exchangeToken(
    publicToken: string,
    id: {
      linkToken?: string;
      linkMetadata?: unknown;
      sessionId?: string;
      phone?: string;
      mode?: 'payment_bank' | 'payouts' | 'credit_only' | 'spending' | 'payment' | 'payout';
      fastLink?: boolean;
      postConnectTarget?: string;
      forceVisualRefresh?: boolean;
      includeBankVisuals?: boolean;
    }
  ) {
    const exchange = httpsCallable(functions, 'exchangePlaidToken');
    const result = await withSentrySpan(
      {
        op: 'plaid.exchange_token',
        name: 'plaid.exchange_token',
        attributes: {
          mode: String(id.mode || 'spending'),
          fastLink: Boolean(id.fastLink),
          postConnectTarget: String(id.postConnectTarget || ''),
        },
      },
      () => exchange({ publicToken, ...id }),
    );
    return result.data as {
      bankDisplayName: string;
      bankLast4: string;
      plaidItemId: string;
      accountId: string;
      accountType: string;
      isPayoutAccount: boolean;
      payoutSelectionRequired: boolean;
      astraAccountId: string | null;
      astraStatus: string | null;
      astraEligibilityStatus: string | null;
    };
  },

  async preparePaymentBankReconnect(input: { plaidAccountId: string }) {
    const prepare = httpsCallable(functions, 'prepareAstraBankReconnect');
    const result = await prepare(input);
    return result.data as { success?: boolean; preparedAccountIds?: string[] };
  },

  async relinkPaymentBank(input: {
    phone: string;
    oldPlaidAccountId: string;
    newPlaidAccountId: string;
  }) {
    const relink = httpsCallable(functions, 'relinkAstraBankAccountForUser');
    const result = await relink(input);
    return result.data as { success?: boolean; accountId?: string | null; status?: string | null };
  },

  async payInvite(
    params: {
      sessionId: string;
      inviteId: string;
      plaidAccountId?: string;
      paymentAmountCents?: number;
      baseAmountCents?: number;
      claimableAcknowledged?: boolean;
    },
    optionsOrSave: boolean | { savePaymentMethod?: boolean; paymentMethodId?: string } = false
  ) {
    const pay = httpsCallable(functions, 'settleSplitWithBank');

    const options = typeof optionsOrSave === 'boolean' ? { savePaymentMethod: optionsOrSave } : optionsOrSave;
    const result = await withSentrySpan(
      {
        op: 'split.payment',
        name: 'split.settle_with_bank',
        attributes: {
          sessionId: String(params.sessionId || ''),
          hasPlaidAccountId: Boolean(params.plaidAccountId),
        },
      },
      () => pay({ ...params, ...options }),
    );
    return result.data as { transferId: string; status: string };
  },

  async withdraw(sessionId: string, amount: number) {
    void sessionId;
    void amount;
    throw new Error('Manual withdrawals are disabled.');
  },

  async disconnectBank(input: { accountId: string; removeEntireItem?: boolean }) {
    const accountId = String(input.accountId || '').trim();
    if (!accountId) {
      throw new Error('Account ID is required to disconnect a Plaid account.');
    }
    const disconnect = httpsCallable(functions, 'disconnectPlaidItem');
    const result = await disconnect({
      accountId,
      ...(input.removeEntireItem === true ? { removeEntireItem: true } : {}),
    });
    return result.data as { success: boolean; message?: string };
  },

  async unlinkPaymentBank(id: { phone: string; bankLinkId?: string }) {
    const unlink = httpsCallable(functions, 'unlinkPaymentBank');
    const result = await unlink(id);
    return result.data as { success: boolean };
  },

  async commitClaimDraft(data: {
    sessionId: string;
    inviteId: string;
    baseSessionVersion: number;
    itemMutations: Array<{
      itemId: string;
      operation: 'claim' | 'unclaim' | 'set_fraction';
      requestedFraction?: number;
      rebalanceMode?: 'equalize';
      baseItemVersion?: number;
    }>;
  }) {
    const commitClaimDraft = httpsCallable(functions, 'commitClaimDraftV1');
    const clientCommitId = `web_commit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const safeMutations = Array.isArray(data.itemMutations) ? data.itemMutations : [];
    const result = await withSentrySpan(
      {
        op: 'split.claim',
        name: 'split.commit_claim_draft',
        attributes: {
          sessionId: String(data.sessionId || ''),
          mutationCount: safeMutations.length,
        },
      },
      () => commitClaimDraft({
        sessionId: data.sessionId,
        inviteId: data.inviteId,
        baseSessionVersion: Number.isFinite(Number(data.baseSessionVersion)) ? Number(data.baseSessionVersion) : 0,
        itemMutations: safeMutations,
        clientCommitId,
      }),
    );
    return result.data as {
      success: boolean;
      mutationResult?: 'applied' | 'conflicted' | 'noop' | 'idempotent';
      applied?: Array<{ itemId: string; operation: string; itemVersion: number; appliedFraction: number }>;
      conflicts?: Array<{ itemId: string; operation: string; reason: string; message: string }>;
      serverItemsPatch?: Array<{ itemId: string; shares: Record<string, any>; version: number }>;
      newSessionVersion?: number;
    };
  },
};

export const astraService = {
  async getOnboardingStatus(phone: string) {
    const fn = httpsCallable(functions, 'getAstraOnboardingStatus');
    const result = await fn({ phone });
    return result.data as {
      phone: string;
      userId?: string | null;
      userStatus?: string | null;
      kycType?: string | null;
      payoutAccountId?: string | null;
      payoutAstraAccountId?: string | null;
      payoutReady?: boolean;
      identityVerificationStatus?: string | null;
      requiresIdentityDocumentVerification?: boolean;
    };
  },

  async startIdentityVerification(input: {
    phone: string;
    legalName: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postalCode: string;
    };
    dob: {
      day: number;
      month: number;
      year: number;
    };
    ssnLast4: string;
    redirectUri?: string;
  }) {
    const fn = httpsCallable(functions, 'startWithdrawKyc');
    const result = await fn(input);
    return result.data as {
      success?: boolean;
      completedViaApi?: boolean;
      requiresAuthorization?: boolean;
      status?: string;
      message?: string;
      onboardingUrl?: string;
      redirectUri?: string;
    };
  },

  async authorizePaymentUser(input: {
    phone: string;
    legalName: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postalCode: string;
    };
    dob: {
      day: number;
      month: number;
      year: number;
    };
    ssnLast4: string;
    consentCaptured: boolean;
    plaidAccountId?: string;
    redirectUri?: string;
  }) {
    const fn = httpsCallable(functions, 'authorizeAstraPaymentUserViaApi');
    const result = await fn(input);
    return result.data as {
      success?: boolean;
      requiresAuthorization?: boolean;
      status?: string;
      message?: string;
      onboardingUrl?: string;
      redirectUri?: string;
      userId?: string | null;
      userStatus?: string | null;
      kycType?: string | null;
      astraAccountId?: string | null;
    };
  },

  async createPaymentAuthorizationLink(input: {
    phone: string;
    legalName: string;
    address: {
      line1: string;
      city: string;
      state: string;
      postalCode: string;
    };
    dob: {
      day: number;
      month: number;
      year: number;
    };
    ssnLast4: string;
    redirectUri?: string;
  }) {
    const fn = httpsCallable(functions, 'createAstraPaymentAuthorizationLink');
    const result = await fn(input);
    return result.data as {
      success?: boolean;
      requiresAuthorization?: boolean;
      onboardingUrl?: string;
      redirectUri?: string;
    };
  },

  async exchangeAuthorizationCode(input: {
    phone: string;
    authorizationCode: string;
    redirectUri?: string;
  }) {
    const fn = httpsCallable(functions, 'exchangeAstraAuthorizationCodeForUser');
    const result = await fn(input);
    return result.data as {
      success?: boolean;
      requestId?: string;
      status?: string;
      onboardingUrl?: string;
      redirectUri?: string;
    };
  },

  async createDocumentVerificationLink(input: {
    phone: string;
    redirectUri?: string;
  }) {
    const fn = httpsCallable(functions, 'createAstraDocumentVerificationLink');
    const result = await fn(input);
    return result.data as {
      success?: boolean;
      status?: string;
      onboardingUrl?: string;
      redirectUri?: string;
    };
  },

  async registerPaymentBank(input: { phone: string; plaidAccountId?: string }) {
    const fn = httpsCallable(functions, 'registerAstraPaymentBank');
    const result = await fn(input);
    return result.data as { success?: boolean; accountId?: string | null; status?: string | null };
  },

  async registerPayoutBank(input: { phone: string; plaidAccountId?: string }) {
    const fn = httpsCallable(functions, 'registerAstraPayoutBank');
    const result = await fn(input);
    return result.data as { success?: boolean; accountId?: string | null; status?: string | null };
  },
};

export const userService = {
  async resolveUidByActivePhone(phone: string): Promise<string | null> {
    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) return null;
    try {
      const indexSnap = await getDoc(doc(firestore, 'phoneIndex', normalizedPhone));
      if (!indexSnap.exists()) return null;
      const indexData = indexSnap.data() as any;
      const status = String(indexData?.status || '').trim().toLowerCase();
      const uid = String(indexData?.activeUid || '').trim();
      if (status !== 'active' || !uid) return null;
      return uid;
    } catch (err) {
      return null;
    }
  },

  async resolveUserDocByPhone(phone: string) {
    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) return null;
    const uid = await this.resolveUidByActivePhone(normalizedPhone);
    if (!uid) return null;
    try {
      const userSnap = await getDoc(doc(firestore, 'users', uid));
      if (!userSnap.exists()) return null;
      return userSnap;
    } catch (err) {
      return null;
    }
  },

  async getUser(phone: string, uidHint?: string | null) {
    const normalizedPhone = normalizePhone(phone);
    if (!normalizedPhone) return null;

    // 1. Try Cloud Function resolveParticipantProfiles (runs with Admin SDK privileges, bypassing client security rules)
    if (auth.currentUser) {
      try {
        const functionsInstance = getFunctions(app, 'us-central1');
        const callable = httpsCallable(functionsInstance, 'resolveParticipantProfiles');
        const res: any = await callable({ phones: [normalizedPhone] });
        const results = Array.isArray(res?.data?.results) ? res.data.results : [];
        const match = results.find((r: any) => normalizePhone(r.phone) === normalizedPhone);
        if (match && (match.name || match.uid)) {
          let photoURL = '';
          const targetUid = match.uid || uidHint;
          if (targetUid) {
            try {
              const cardSnap = await getDoc(doc(firestore, 'profileCards', targetUid));
              if (cardSnap.exists()) {
                photoURL = cardSnap.data()?.photoURL || '';
              }
            } catch (e) {}
          }
          return {
            id: match.uid || normalizedPhone,
            name: match.name,
            photoURL,
            hasCleroAccount: Boolean(match.hasCleroAccount)
          };
        }
      } catch (err) {}
    }

    // 2. Exact UID profile lookup; legacy phone-keyed profiles are private.
    const candidateUids = [uidHint].filter(Boolean) as string[];
    for (const uid of candidateUids) {
      try {
        const profileSnap = await getDoc(doc(firestore, 'profiles', uid));
        if (profileSnap.exists()) {
          const data = profileSnap.data() || {};
          const cardSnap = await getDoc(doc(firestore, 'profileCards', uid)).catch(() => null);
          const card = cardSnap?.data() || {};
          const name = resolveCanonicalIdentityName(data, normalizedPhone);
          return {
            id: uid,
            ...data,
            name: name || data.name || data.displayName || null,
            photoURL: card.photoURL || ''
          };
        }
      } catch (e) {}
    }

    // 3. Fallback: try owner read if viewer is owner of user doc
    try {
      const docSnap = await this.resolveUserDocByPhone(normalizedPhone);
      if (docSnap && docSnap.exists()) {
        const data = docSnap.data() || {};
        const resolvedName = resolveCanonicalIdentityName(data);
        return {
          id: docSnap.id,
          ...data,
          name: resolvedName || null,
        } as any;
      }
    } catch (e) {}

    return null;
  },

  async createOrUpdateUser(phone: string, data: any) {
    const normalizedPhone = normalizePhone(phone);
    const currentUid = String(auth.currentUser?.uid || '').trim();
    const existingDoc = await this.resolveUserDocByPhone(normalizedPhone);
    const docRef = existingDoc ? existingDoc.ref : (currentUid ? doc(firestore, 'users', currentUid) : null);
    if (!docRef) {
      throw new Error('Unable to resolve user uid for profile upsert.');
    }
    const existing = existingDoc || await getDoc(docRef);

    // Prepare data to include index for querying
    const updates = { ...data };
    const incomingCanonicalName = resolveCanonicalIdentityName(updates);
    const existingCanonicalName = resolveCanonicalIdentityName(existing.data());
    const mergedName = (incomingCanonicalName || existingCanonicalName || '') as string;
    const canonicalFields = buildCanonicalIdentityFields(
      mergedName || updates.name || existing.data()?.name || existing.data()?.displayName || ''
    );
    if (canonicalFields.name) {
      updates.name = canonicalFields.name;
    }
    if (canonicalFields.firstName) {
      updates.firstName = canonicalFields.firstName;
    }
    if (canonicalFields.lastName || Object.prototype.hasOwnProperty.call(updates, 'lastName')) {
      updates.lastName = canonicalFields.lastName;
    }
    const mergedUsername = (updates.username ?? existing.data()?.username ?? '') as string;
    if (mergedName || mergedUsername) {
      updates.searchTokens = buildProfileSearchTokens(mergedName, mergedUsername);
    }
    if (data.savedFriends) {
      updates.savedFriendPhones = data.savedFriends.map((f: any) => normalizePhone(f.phone));
    }

    if (existing.exists()) {
      await updateDoc(docRef, updates);
      return { id: docRef.id, ...existing.data(), ...updates };
    } else {
      // NEW USER: Reciprocal linking
      const reciprocalFriends: any[] = [];
      const reciprocalFriendPhones: string[] = [];

      try {
        // Find everyone who has THIS phone in their savedFriendPhones
        const q = query(collection(firestore, 'users'), where('savedFriendPhones', 'array-contains', normalizedPhone));
        const snap = await getDocs(q);

        snap.forEach(d => {
          const userData = d.data();
          reciprocalFriends.push({
            name: resolveCanonicalIdentityName(userData) || 'Friend',
            phone: normalizePhone(String(userData.phone || ''))
          });
          reciprocalFriendPhones.push(normalizePhone(String(userData.phone || '')));
        });
      } catch (err) {
        console.error('Error finding reciprocal friends:', err);
      }

      const finalData = {
        phone: normalizedPhone,
        ...updates,
        savedFriends: [...(updates.savedFriends || []), ...reciprocalFriends],
        savedFriendPhones: [...(updates.savedFriendPhones || []), ...reciprocalFriendPhones],
        trustedDevices: updates.trustedDevices || [],
        createdAt: serverTimestamp(),
      };

      await setDoc(docRef, finalData);
      return { id: docRef.id, ...finalData };
    }
  },

  async deleteUserByPhone(phone: string) {
    void phone;
    throw new Error('Client account deletion is disabled. Use authenticated server account-deletion workflow.');
  }
};

export const verificationService = {
  async createVerification(phone: string) {
    const normalizedPhone = normalizePhone(phone);
    const verificationsCol = collection(firestore, 'verifications');
    const docRef = await addDoc(verificationsCol, {
      phone: normalizedPhone,
      createdAt: serverTimestamp(),
      isVerified: false
    });
    return docRef.id;
  },

  subscribeToVerification(verificationId: string, callback: (data: any) => void) {
    const docRef = doc(firestore, 'verifications', verificationId);
    return onSnapshot(docRef, (d) => {
      if (d.exists()) {
        callback({ id: d.id, ...d.data() });
      }
    });
  },

  async markVerified(verificationId: string) {
    const docRef = doc(firestore, 'verifications', verificationId);
    await updateDoc(docRef, { isVerified: true });
  }
};


// reCAPTCHA SHARED VERIFIER SERVICE
// Prevents multiple components from fighting over the same DOM element
// and ensures a single consistent verifier lifecycle.
let sharedVerifier: RecaptchaVerifier | null = null;
let sharedVerifierHostId: string | null = null;
let sharedVerifierTarget: HTMLDivElement | null = null;

export const getSharedRecaptchaVerifier = (
  containerId: string = 'recaptcha-container',
  authInstance?: any,
) => {
  if (typeof window === 'undefined') return null;

  try {
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`[reCAPTCHA] Container #${containerId} not found.`);
      return null;
    }

    // If we already have a valid verifier on this container, reuse it.
    if (sharedVerifier && sharedVerifierHostId === containerId) {
      return sharedVerifier;
    }

    // Clear previous verifier instance safely before creating a new one
    if (sharedVerifier) {
      try { sharedVerifier.clear(); } catch (e) { }
      sharedVerifier = null;
      container.innerHTML = '';
    }

    const targetAuth = authInstance || auth;
    sharedVerifier = new RecaptchaVerifier(targetAuth, containerId, {
      'size': 'invisible',
      'callback': (response: any) => {
        console.log('[reCAPTCHA] Verification successful');
      },
      'expired-callback': () => {
        console.warn('[reCAPTCHA] Verification expired');
      }
    });

    sharedVerifierHostId = containerId;
    (window as any).recaptchaVerifier = sharedVerifier;
    return sharedVerifier;
  } catch (err) {
    console.error('[reCAPTCHA] Initialization failed:', err);
    return null;
  }
};

export const resetSharedRecaptchaVerifier = (containerId: string = 'recaptcha-container') => {
  if (typeof window === 'undefined') return null;

  try {
    if (sharedVerifier) {
      try { sharedVerifier.clear(); } catch (e) { }
      sharedVerifier = null;
    }
    sharedVerifierHostId = null;
    sharedVerifierTarget = null;
    (window as any).recaptchaVerifier = null;

    const container = document.getElementById(containerId);
    if (container) {
      // Defer DOM reset to allow active reCAPTCHA script callbacks to finish cleanly
      setTimeout(() => {
        try {
          if (container && !sharedVerifier) {
            container.innerHTML = '';
          }
        } catch { }
      }, 150);
    }

    return null;
  } catch (err) {
    console.error('[reCAPTCHA] Reset failed:', err);
    return null;
  }
};

export const formatPhoneAuthError = (err: any) => {
  const code = err?.code || '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isKnownHost =
    hostname === 'clero.money' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.vercel.app') ||
    hostname.endsWith('.firebaseapp.com');

  if (code === 'auth/too-many-requests') {
    return 'Too many verification attempts. Please wait a few minutes and try again.';
  }

  if (code === 'auth/captcha-check-failed' || code === 'auth/invalid-app-credential') {
    if (!isKnownHost) {
      return `Domain (${hostname}) is not authorized for phone verification in Firebase Console. Please test on clero.money or localhost.`;
    }
    return 'Verification connection failed. Please refresh the page and try again.';
  }

  return err?.message || 'Failed to send verification code';
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs = 8000, errorMsg = 'Operation timed out'): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(errorMsg)), timeoutMs)
    )
  ]);
};

// Compatibility export
export const db = {
  collection: (path: string) => ({
    path,
    add: async (data: any) => {
      const docRef = await withTimeout(addDoc(collection(firestore, path), data), 8000, 'Firestore add timed out');
      return { id: docRef.id };
    },
    doc: (docId: string) => ({
      path: `${path}/${docId}`,
      id: docId,
      set: async (data: any, options?: { merge?: boolean }) => {
        const docRef = doc(firestore, path, docId);
        await withTimeout(setDoc(docRef, data, options || {}), 8000, 'Firestore set timed out');
        return { id: docId };
      }
    })
  })
};
